/**
 * Utility functions for streak reminder modal timing and display logic
 */

const STORAGE_KEY = "deathx-streak-reminder-last-shown";
const RECOVERY_STORAGE_KEY = "deathx-recovery-reminder-last-shown";
const REMINDER_COOLDOWN_MS = 60 * 60 * 1000; // 1 hour in milliseconds

/**
 * Get the last time the streak reminder was shown from localStorage
 * @returns timestamp in milliseconds, or null if never shown
 */
export const getLastReminderTime = (): number | null => {
  try {
    const storedTime = localStorage.getItem(STORAGE_KEY);
    if (storedTime) {
      return parseInt(storedTime, 10);
    }
    return null;
  } catch (error) {
    console.error("Error getting last reminder time:", error);
    return null;
  }
};

/**
 * Store the current timestamp as the last reminder time
 */
export const setReminderTime = (): void => {
  try {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
  } catch (error) {
    console.error("Error setting reminder time:", error);
  }
};

/**
 * Check if enough time has passed since the last reminder to show it again
 * @returns true if reminder can be shown, false if still in cooldown
 */
export const shouldShowReminder = (): boolean => {
  const lastReminderTime = getLastReminderTime();
  
  if (lastReminderTime === null) {
    return true; // Never shown before
  }
  
  const timeSinceLastReminder = Date.now() - lastReminderTime;
  return timeSinceLastReminder >= REMINDER_COOLDOWN_MS;
};

/**
 * Get today's date in YYYY-MM-DD format for comparison with lastGameDate
 * @returns today's date string in YYYY-MM-DD format
 */
export const getTodayDateString = (): string => {
  return new Date().toISOString().split('T')[0];
};

/**
 * Get yesterday's date in YYYY-MM-DD format for comparison with lastGameDate
 * @returns yesterday's date string in YYYY-MM-DD format
 */
export const getYesterdayDateString = (): string => {
  const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000); // Subtract 24 hours in milliseconds
  return yesterday.toISOString().split('T')[0];
};

/**
 * Main logic to determine if the streak reminder modal should be shown
 * @param currentStreak user's current streak count
 * @param lastGameDate user's last game date in YYYY-MM-DD format
 * @returns true if modal should be shown, false otherwise
 */
export const shouldShowStreakReminder = (currentStreak: number, lastGameDate: string): boolean => {
  // Don't show if no active streak
  if (currentStreak <= 0) {
    return false;
  }
  
  // Only show if user's last game was yesterday (streak is maintained but at risk)
  const yesterday = getYesterdayDateString();
  if (lastGameDate !== yesterday) {
    return false;
  }
  
  // Don't show if still in cooldown period
  if (!shouldShowReminder()) {
    return false;
  }
  
  return true;
};

/**
 * Clear the stored reminder time (for testing purposes)
 */
export const clearReminderTime = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error("Error clearing reminder time:", error);
  }
};

// ===============================
// Recovery Reminder Functions
// ===============================

/**
 * Get the last time the recovery reminder was shown from localStorage
 * @returns timestamp in milliseconds, or null if never shown
 */
export const getLastRecoveryReminderTime = (): number | null => {
  try {
    const storedTime = localStorage.getItem(RECOVERY_STORAGE_KEY);
    if (storedTime) {
      return parseInt(storedTime, 10);
    }
    return null;
  } catch (error) {
    console.error("Error getting last recovery reminder time:", error);
    return null;
  }
};

/**
 * Store the current timestamp as the last recovery reminder time
 */
export const setRecoveryReminderTime = (): void => {
  try {
    localStorage.setItem(RECOVERY_STORAGE_KEY, Date.now().toString());
  } catch (error) {
    console.error("Error setting recovery reminder time:", error);
  }
};

/**
 * Check if enough time has passed since the last recovery reminder to show it again
 * @returns true if recovery reminder can be shown, false if still in cooldown
 */
export const shouldShowRecoveryReminder = (): boolean => {
  const lastReminderTime = getLastRecoveryReminderTime();
  
  if (lastReminderTime === null) {
    return true; // Never shown before
  }
  
  const timeSinceLastReminder = Date.now() - lastReminderTime;
  return timeSinceLastReminder >= REMINDER_COOLDOWN_MS;
};

/**
 * Main logic to determine if the recovery reminder modal should be shown
 * @param eligibleToRecover whether user is eligible to recover their streak
 * @returns true if modal should be shown, false otherwise
 */
export const shouldShowRecoveryReminderModal = (eligibleToRecover: boolean): boolean => {
  // Don't show if user is not eligible to recover
  if (!eligibleToRecover) {
    return false;
  }
  
  // Don't show if still in cooldown period
  if (!shouldShowRecoveryReminder()) {
    return false;
  }
  
  return true;
};

/**
 * Clear the stored recovery reminder time (for testing purposes)
 */
export const clearRecoveryReminderTime = (): void => {
  try {
    localStorage.removeItem(RECOVERY_STORAGE_KEY);
  } catch (error) {
    console.error("Error clearing recovery reminder time:", error);
  }
};